/**
 * FO-011 (batch-13) — gedeelde bron voor de factoring-/cessietekst op de factuur.
 *
 * Eén bron van waarheid voor:
 *   - de Finqle-IBAN (vast, uit FO-011) die op via-Finqle-facturen getoond wordt;
 *   - de VOORBEELD-default cessieclausule (gebruikt als het BV-veld
 *     `factuurFooterTekst` leeg is);
 *   - het invullen van de placeholders.
 *
 * Wordt gedeeld door de factuur-PDF (`apps/api/src/lib/invoice-pdf.ts`), de
 * UBL/XML-mapping (`invoice-xml-mapping.ts`) én de live preview in de
 * BV-instellingen (`apps/backoffice/.../entiteiten`) zodat PDF, XML en preview
 * nooit uiteenlopen. Pure module: geen Prisma/I/O-afhankelijkheid.
 */

/**
 * Finqle's vaste incasso-IBAN (uit FO-011), display-vorm met spaties zoals op de
 * factuur getoond. De STREEP-eigen IBAN komt uit de BV-instelling en wordt bij
 * factoring NOOIT getoond - alleen deze Finqle-IBAN.
 */
export const FINQLE_IBAN_DISPLAY = 'NL08 ABNA 0870 3740 44'

/** Finqle-IBAN canoniek (zonder spaties) - voor de UBL/XML PayeeFinancialAccount. */
export const FINQLE_IBAN = 'NL08ABNA0870374044'

/** Finqle's incassant-naam (UBL PayeeParty + cessieclausule). */
export const FINQLE_PAYEE_NAME = 'Finqle B.V.'

/**
 * De placeholders die in de factoringtekst worden ingevuld (voor de
 * placeholder-hulp in de UI). Volgorde = leesvolgorde in de default-clausule.
 */
export const FACTORING_PLACEHOLDERS = [
  '[Entiteit]',
  '[factuur_betaaltermijn]',
  '[factuur_betalen_voor]',
  '[factuurnummer]',
] as const

/**
 * VOORBEELD-default factoring-/cessieclausule (Finqle). INSTELBAAR per BV via
 * `Entity.factuurFooterTekst`; deze constante is alleen de fallback wanneer dat
 * veld leeg is. De Finqle-IBAN + incassant-naam komen uit de constanten hierboven
 * (één bron). Geen em-dash.
 */
export const DEFAULT_FACTORING_FOOTER =
  `[Entiteit] maakt gebruik van factoring. In verband daarmee heeft deze het ` +
  `vorderingsrecht van (het vrije gedeelte van) deze factuur overgedragen (verkocht ` +
  `en gecedeerd) aan ${FINQLE_PAYEE_NAME} te Amsterdam. (Het vrije gedeelte van) deze factuur ` +
  `kan uitsluitend bevrijdend worden betaald aan ${FINQLE_PAYEE_NAME} Betaling dient binnen ` +
  `[factuur_betaaltermijn] na dagtekening van deze factuur te geschieden ` +
  `[factuur_betalen_voor] door overboeking op IBAN ${FINQLE_IBAN_DISPLAY} ten name ` +
  `van ${FINQLE_PAYEE_NAME} te Amsterdam o.v.v. het factuurnummer.`

/** Ingevulde waarden voor de factoring-footer-placeholders. */
export interface FactoringFooterPlaceholders {
  /** [Entiteit] - naam van de uitvoerende STREEP-BV. */
  entiteit: string
  /** [factuur_betaaltermijn] - bv. "30 dagen". */
  betaaltermijn: string
  /** [factuur_betalen_voor] - bv. "(uiterlijk 03-07-2026)". */
  betalenVoor: string
  /** [factuurnummer] - definitief factuurnummer of conceptnummer. */
  factuurnummer: string
}

/**
 * Vul de factoring-footer-placeholders in. Globale replace zodat een placeholder
 * meerdere keren in de tekst mag voorkomen. Eén implementatie voor PDF, XML en
 * de instellingen-preview.
 */
export function resolveFactoringFooter(
  template: string,
  p: FactoringFooterPlaceholders,
): string {
  return template
    .replace(/\[Entiteit\]/g, p.entiteit)
    .replace(/\[factuur_betaaltermijn\]/g, p.betaaltermijn)
    .replace(/\[factuur_betalen_voor\]/g, p.betalenVoor)
    .replace(/\[factuurnummer\]/g, p.factuurnummer)
}
